import { useState } from 'react'
import { RiAddLine, RiSubtractLine } from 'react-icons/ri'
import { useInView } from '@/hooks'

const faqs = [
  {
    question: 'What is a doula, and what do you do?',
    answer:
      'A doula is a trained professional who provides continuous emotional, physical, and informational support to birthing parents before, during, and after childbirth. As an antenatal and postpartum doula, I guide you through pregnancy, help you prepare a birth plan, offer comfort techniques so you or your partner can use during labour, and support your family in the early weeks of parenthood. My role is non-medical, meaning I complement the care of your healthcare providers, ensuring you feel informed, confident, and supported throughout your journey.',
  },
  {
    question: 'Do doulas replace my midwife?',
    answer:
      'No — doulas do not provide medical care. I work alongside your midwife or other healthcare providers to ensure your needs are supported holistically. While I cannot diagnose, prescribe, or perform medical procedures, I do offer continuous emotional and practical support, help you understand your options, and provide comfort techniques that enhance your medical care experience.',
  },
  {
    question: 'What is the difference between an antenatal and a postpartum doula?',
    answer:
      'An antenatal doula focuses on preparing you and your partner for birth. This includes prenatal education, birth planning, comfort techniques, and emotional support leading up to labour. A postpartum doula provides hands-on support after your baby arrives, assisting with newborn care, breastfeeding guidance, emotional well-being, household help, and parenting education. Many families choose to work with a doula both before and after birth for seamless, continuous support.',
  },
  {
    question: 'How do you work with partners or family members?',
    answer:
      'I see partners and family members as essential members of the birth team. I offer guidance on how they can support the mother, provide comfort techniques they can use, and help them feel confident and included in the process. My goal is to empower the whole family so that everyone feels supported and engaged, while still respecting individual roles and dynamics.',
  },
  {
    question: 'Do you only support first-time births?',
    answer:
      "Not at all! I support parents of all experiences — whether it's your first child or your fourth. Every birth is unique, and I'm here to provide personalised guidance, reassurance, and advocacy for families at any stage of their parenting journey.",
  },
  {
    question: "What if I don't have a vaginal birth? Can I still hire a doula?",
    answer:
      'Yes of course! Doulas support all types of births, including cesareans, inductions, or high-intervention deliveries. For an elective or emergency C-Section, I still provide you with the emotional support and assist with preparation. After birth, I continue to provide postpartum care, helping you bond with your baby, manage recovery, and adjust to parenthood, regardless of how your birth unfolds.',
  },
  {
    question: 'Do you support parents who have had a previous traumatic birth?',
    answer:
      'Yes, absolutely. I provide compassionate support for anyone who has experienced a difficult or traumatic birth in the past. My role is to help you feel empowered, heard, and safe throughout your pregnancy and postpartum journey. Together, we can create a birth experience that honours your needs, supports healing, and fosters confidence.',
  },
  {
    question: 'When should I hire a doula?',
    answer:
      "Ideally, hiring a doula happens in the second trimester or early third trimester for birth support, giving us time to build a trusting relationship, discuss your preferences, and create a personalised birth plan. My antenatal services start as early as 24 weeks of your pregnancy. My Postnatal services can begin right after birth, depending on your need. The earlier we start, the more prepared and supported you'll feel throughout pregnancy, birth, and early parenthood.",
  },
  {
    question: 'What is your availability and how do we schedule sessions?',
    answer:
      "Availability varies depending on your due date and postpartum needs. Typically, we begin with an initial consultation to discuss your goals, preferences, and schedule. During the prenatal period, we meet for a few sessions to prepare for birth. I offer online virtual or face-to-face antenatal sessions and schedule postpartum visits based on your family's needs. Flexibility is key — I tailor support to fit your schedule and lifestyle.",
  },
]

interface FAQItemProps {
  question: string
  answer: string
  isOpen: boolean
  onToggle: () => void
  index: number
  inView: boolean
}

const FAQItem = ({ question, answer, isOpen, onToggle, index, inView }: FAQItemProps) => (
  <div
    className={`border border-base-200 rounded-2xl overflow-hidden transition-all duration-200 ${inView ? 'animate-fade-in-up' : 'opacity-0'}`}
    style={{ animationDelay: inView ? `${0.05 * index}s` : undefined }}
  >
    <button
      onClick={onToggle}
      className={`w-full flex items-center justify-between gap-4 px-6 py-5 text-left transition-colors duration-200 ${
        isOpen ? 'bg-crimson-50' : 'bg-white hover:bg-base-50'
      }`}
      aria-expanded={isOpen}
    >
      <span
        className={`font-body font-semibold text-[15px] leading-snug transition-colors duration-200 ${
          isOpen ? 'text-crimson-700' : 'text-base-900'
        }`}
      >
        {question}
      </span>
      <span
        className={`shrink-0 w-7 h-7 rounded-full flex items-center justify-center transition-all duration-200 ${
          isOpen ? 'bg-crimson-600 text-white' : 'bg-base-100 text-base-500'
        }`}
      >
        {isOpen ? <RiSubtractLine size={15} /> : <RiAddLine size={15} />}
      </span>
    </button>

    <div
      className={`overflow-hidden transition-all duration-300 ease-in-out ${
        isOpen ? 'max-h-[600px] opacity-100' : 'max-h-0 opacity-0'
      }`}
    >
      <p className="px-6 pt-3 pb-6 text-[14.5px] text-base-600 leading-[1.85] border-t border-base-100">
        {answer}
      </p>
    </div>
  </div>
)

export const FAQSection = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(null)
  const { ref, inView } = useInView<HTMLDivElement>()

  const toggle = (i: number) => setOpenIndex(openIndex === i ? null : i)

  return (
    <section className="bg-base-50 py-20 lg:py-28 overflow-hidden">
      <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
        {/* Header */}
        <div ref={ref} className="text-center mb-14 space-y-4">
          <h2
            className={`font-display font-bold text-base-900 leading-tight ${inView ? 'animate-fade-in-up' : 'opacity-0'}`}
            style={{
              fontSize: 'clamp(1.85rem, 4vw, 3rem)',
              animationDelay: inView ? '0.1s' : undefined,
            }}
          >
            Answers To Help You Feel{' '}
            <em className="text-crimson-600 not-italic italic">Confident & Prepared</em>
          </h2>

          <div
            className={`w-12 h-[3px] rounded-full bg-gradient-to-r from-crimson-600 to-gold-500 mx-auto ${inView ? 'animate-fade-in' : 'opacity-0'}`}
            style={{ animationDelay: inView ? '0.2s' : undefined }}
          />
        </div>

        {/* FAQ accordion — single column */}
        <div className="flex flex-col gap-3 max-w-3xl mx-auto mb-16">
          {faqs.map((faq, i) => (
            <FAQItem
              key={faq.question}
              question={faq.question}
              answer={faq.answer}
              isOpen={openIndex === i}
              onToggle={() => toggle(i)}
              index={i}
              inView={inView}
            />
          ))}
        </div>
      </div>
    </section>
  )
}
